import { AddImageCard } from 'components/addImageCard'
import { Button } from 'components/buttons'
import { initialStateMenu, initialStateModal } from 'components/dynamicTab'
import { Icon } from 'components/icon'
import { Input } from 'components/input'
import { ModalConfirm } from 'components/modal'
import { handleValid } from 'components/myOrganization'
import { SetUpInfoTab } from 'components/setUpInfoTab'
import { Slide } from 'components/slide'
import { FormResourceOrganization } from 'components/subMenuTabItem'
import { Typography } from 'components/typography'
import { Icons, TabMyOrganizationItem, TypographyTypes } from 'interfaces'
import React, { FC, useState } from 'react'
import {
	Controller,
	Path,
	useFieldArray,
	UseFieldArrayRemove,
	useWatch
} from 'react-hook-form'
import { configGenaral } from 'utils'

interface TabManagerCardsProps extends Partial<FormResourceOrganization> {
	active: number
	deletedTab: (pos: number) => void
	remove: UseFieldArrayRemove
	fieldError: string[]
	setFieldError: React.Dispatch<React.SetStateAction<string[]>>
	nameSection: string
	resourceUpdated: boolean
}

export const TabManagerCards: FC<TabManagerCardsProps> = (
	props
): JSX.Element => {
	const {
		active,
		errors,
		control,
		register,
		setValue,
		watch,
		deletedTab,
		remove: removeComponent,
		fieldError,
		nameSection,
		resourceUpdated,
		setFieldError
	} = props

	const [modalProps, setModalProps] = useState(initialStateModal)
	const [openSlideProfile, setOpenSlideProfile] = useState(false)
	const rules = {
		name: {
			required: { value: true, message: 'This field is required' }
		}
	}
	const hasImage = watch?.(`tabs.${active}.image_card`)
	const enabledLink = useWatch({
		control,
		name: `tabs.${active}.setup_card.link.enabled_link`
	})

	const enabledMenu = useWatch({
		control,
		name: `tabs.${active}.setup_card.menu.enabled_menu`
	})

	const components =
		watch?.(
			`tabs.${active}.components` as Path<{
				tabs: TabMyOrganizationItem[]
			}>
		) ?? []
	const { fields, append, remove } = useFieldArray({
		control,
		name: `tabs.${active}.setup_menu`
	})

	const handleDelectExternalLink = (): void => {
		const routeLink = `tabs.${active}.setup_card.link`
		setValue?.(
			`${routeLink}.url` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			''
		)
		setValue?.(
			`${routeLink}.enabled_link` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			false
		)
		setModalProps(initialStateModal)
	}
	const handleDelectMenu = (): void => {
		const nameType = `tabs.${active}.setup_menu`
		const updatedErrors = fieldError.filter(error => !error.includes(nameType))
		setFieldError(updatedErrors)
		handleValid(nameType, true, setFieldError)
		const routeMenu = `tabs.${active}.setup_card.menu.enabled_menu`
		remove()
		setValue?.(
			`${routeMenu}` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			false
		)
		setModalProps(initialStateModal)
	}

	const confirmChangeStateLink = (): void => {
		setModalProps({
			open: true,
			title: 'External Redirection will be deleted!',
			description:
				'By disabling the External Redirection it will be deleted, do you want to continue?',
			onConfirm: () => {
				handleDelectExternalLink()
			}
		})
	}

	const confirmChangeStateMenu = (): void => {
		setModalProps({
			open: true,
			title: 'Menu will be deleted!',
			description:
				'By disabling the menu all contents and menu customization will be deleted, do you want to continue?',
			onConfirm: () => {
				handleDelectMenu()
			}
		})
	}
	const confirmRemoveImage = (): void => {
		setModalProps({
			open: true,
			title: 'Card Image will be deleted!',
			description: 'The card image will be deleted, do you want to continue?',
			onConfirm: () => {
				setValue?.(`tabs.${active}.image_card`, '')
				setModalProps(initialStateModal)
			}
		})
	}

	const handleEnabledMenu = (value: boolean): void => {
		const maxOrder = Math.max(...fields.map(c => c.section_order as number), 0)
		append({ ...initialStateMenu, section_order: maxOrder + 1 })
		removeComponent()
		setValue?.(
			`tabs.${active}.setup_card.menu.enabled_menu` as Path<{
				tabs: TabMyOrganizationItem[]
			}>,
			value
		)
		setModalProps(initialStateModal)
	}

	const confirmHandleEnabledMenu = (value: boolean): void => {
		setModalProps({
			open: true,
			title: 'Components Will Be Deleted',
			description:
				'By enabling the Menu option all components will be deleted, do you want to continue?',
			onConfirm: () => {
				handleEnabledMenu(value)
			}
		})
	}

	const confirmHandleEnabledRediretion = (value: boolean): void => {
		setModalProps({
			open: true,
			title: 'Components Will Be Deleted',
			description:
				'By enabling the External Redirection option all components will be deleted, do you want to continue?',
			onConfirm: () => {
				removeComponent()
				setValue?.(`tabs.${active}.setup_card.link.enabled_link`, value)
				setModalProps(initialStateModal)
			}
		})
	}

	return (
		<div
			className="px-4 pb-6 rounded-lg bg-white border-[1px] border-blue-primary"
			style={{ boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.1)' }}
		>
			<div className="w-full py-3  flex justify-between items-center">
				<Typography
					title={`${nameSection} ${active + 1}`}
					fontLeading="22/27"
					type={TypographyTypes.h2}
					className="text-gray-16"
				/>
			</div>
			<div className="flex items-center">
				<Input
					name={`tabs.${active}.name`}
					min={1}
					register={register}
					rules={rules.name}
					error={errors?.tabs?.[active]?.name}
					borderFull={false}
					inputClassName="rounded-lg border-[1px] border-gray-17"
					limit
					cantLimit={configGenaral.maxTabNumber}
					setValueInput={setValue}
					autoFocus
					watchValue={watch?.(`tabs.${active}.name`)}
				/>
				<div
					aria-hidden="true"
					className="cursor-pointer"
					onClick={() => deletedTab(active)}
				>
					<Icon
						src={Icons.delete}
						className="w-7  ml-3 text-gray-1 "
						fillPath
					/>
				</div>
			</div>
			{resourceUpdated && (
				<>
					<Controller
						control={control}
						name={`tabs.${active}.setup_card.link.enabled_link`}
						render={({ field: { value, onChange } }) => {
							return (
								<SetUpInfoTab
									title="Set external URL:"
									subTitle="External Redirection"
									icon={Icons.portal}
									value={value}
									setValue={enabledUrl => {
										if (!enabledUrl) {
											confirmChangeStateLink()
										} else if (
											Array.isArray(components) &&
											components.length > 0
										) {
											confirmHandleEnabledRediretion(enabledUrl)
										} else {
											onChange(enabledUrl)
										}
									}}
									disabled={enabledMenu}
								/>
							)
						}}
					/>
					<Controller
						control={control}
						name={`tabs.${active}.setup_card.menu.enabled_menu`}
						render={({ field: { value } }) => {
							return (
								<SetUpInfoTab
									title="Set Up Navigation:"
									subTitle="Enable Menu"
									icon={Icons.listNotBox}
									value={value}
									setValue={valueSwicth => {
										if (valueSwicth) {
											if (Array.isArray(components) && components.length > 0) {
												confirmHandleEnabledMenu(valueSwicth)
											} else {
												handleEnabledMenu(valueSwicth)
											}
										} else {
											confirmChangeStateMenu()
										}
									}}
									disabled={enabledLink}
								/>
							)
						}}
					/>

					<div className="mt-4">
						<div className="flex flex-column justify-between">
							<div>
								<div className="flex items-center gap-2">
									<Typography
										title={`Set Card's Image:`}
										fontLeading="12/15"
										type={TypographyTypes.h2}
										className="text-gray-16"
									/>
									{!hasImage && (
										<div className="mr-1 w-4 h-4">
											<Icon
												src={Icons.error}
												className="w-7 ml-3 text-red-500"
												fillPath
											/>
										</div>
									)}
								</div>
								{!!hasImage && (
									<div className="flex items-center gap-2 justify-between mt-2">
										<div className="w-[50%]">
											<img
												src={hasImage}
												alt="Cover"
												className="!object-contain"
											/>
										</div>
										<div
											aria-hidden="true"
											className=" cursor-pointer flex items-center w-full md:w-auto"
											onClick={confirmRemoveImage}
										>
											<Icon
												src={Icons.delete}
												className="w-7 ml-3 text-gray-1 "
												fillPath
											/>
											<Typography
												title="Remove"
												fontLeading="12/12"
												type={TypographyTypes.h2}
												className="text-gray-1"
											/>
										</div>
									</div>
								)}
							</div>
						</div>

						{!hasImage && (
							<div className="flex justify-end">
								<Button
									label="Upload Image"
									onClick={() => {
										setOpenSlideProfile(true)
									}}
									className="w-full md:w-auto mt-4"
								/>
							</div>
						)}
					</div>
				</>
			)}

			<ModalConfirm
				open={modalProps.open}
				setOpen={() => setModalProps(initialStateModal)}
				hasCancel
				buttonLabelCancel="Cancel"
				buttonLabel="Yes"
				flexButton="row"
				handleCancel={() => setModalProps(initialStateModal)}
				onClickConfirm={modalProps.onConfirm}
				icon={Icons.warningActive}
				title={modalProps.title}
				descripcion={modalProps.description}
				classNameButton="h-[32px] w-[151px] m-0 !py-0"
			/>

			<Slide
				open={openSlideProfile}
				setOpen={setOpenSlideProfile}
				title="Set Card's Image:"
				helper
				titleHelper="Supported formats (JPG, JPEG, PNG). Max image size 10 MB. Min. Resolution: 1500 by 488 pixels"
			>
				<Controller
					control={control}
					name={`tabs.${active}.image_card`}
					render={({ field: { value, onChange } }) => {
						return (
							<AddImageCard
								setOpen={setOpenSlideProfile}
								image={value}
								setImage={onChange}
							/>
						)
					}}
				/>
			</Slide>
		</div>
	)
}
